/**
 * AnnouncementBanner (Feature A Inc-2, STATUS-announcements §5) — the app-top,
 * admin-authored announcement banner. Rendered from the reserved HeaderBanner
 * slot (app top, all pages) beside ReadOnlyModeBanner + CalendarReconnectBanner.
 *
 * Self-gating (the CalendarReconnectBanner / ReadOnlyModeBanner precedent):
 * renders nothing unless there's an instanceId AND the user is on an operator
 * pack in CRM mode AND /crm/announcements/active returns at least one
 * not-yet-dismissed announcement.
 *
 * The server already returns exactly the caller's visible set (instance-wide OR
 * user-community ∈ target set) inside the active window, newest-first (D8/D4/§0
 * A1) — this component does NOT re-filter audience or window; it only applies
 * localStorage dismissal (D3) and the cap-3 stack (D4).
 *
 * Each announcement renders through SkyMessageBar (D1 intent → color/icon for
 * free, the ReadOnlyModeBanner precedent). Dismissal (D3) persists in
 * localStorage keyed <id>:<updated_at> so editing an announcement (which bumps
 * updated_at) re-shows it; dismissible=false renders no ✕ (standing banner).
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { X as DismissIcon } from "lucide-react";
import { SkyButton, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { getInstanceIdFromUrl } from "../../utils/instanceId";
import { usePacks } from "../packs/PackContext";
import {
  useGetActiveAnnouncementsQuery,
  type ActiveAnnouncement,
} from "../../store/api/crmAnnouncementsApi";

/** D4 — never render more than this many stacked banners at once. */
const MAX_STACKED = 3;

/** localStorage prefix for per-device dismissal state (D3). */
const DISMISS_PREFIX = "skycrm-announcement-dismissed";

/**
 * Horizontally center the message group (icon + title + body + link) within the
 * full-width bar. SkyMessageBar exposes no alignment prop, so we scope Tailwind
 * arbitrary variants onto its internal layout rather than fighting the library:
 *   - `[&>div]:justify-center` centers the content row (the container's single
 *     child) on its main axis, so the icon + text column sit as a centered group
 *     instead of flush-left.
 *   - `[&>div>div]:flex-initial` stops the text column growing to fill the row
 *     (the library gives it `flex-1`), so it shrinks to its content — yet still
 *     wraps multi-line via the library's own `min-w-0`.
 *   - `[&>div>div]:text-center` centers the title/body/link text within it.
 */
const CENTER_CONTENT = "[&>div]:justify-center [&>div>div]:flex-initial [&>div>div]:text-center";

/**
 * Enlarge the banner title well above SkyMessageBar's default (`text-sm`, 14px).
 * SkyMessageBar renders the `title` prop as a `<p className="font-semibold
 * text-sm">` — the ONLY `<p>` in the bar (the body is a `<div>`/`<span>`). It
 * exposes no size prop, so we scope a Tailwind arbitrary variant onto that title.
 *
 * Robustness / why the earlier attempts read as "unchanged": the previous
 * variant (`[&>div>div>p]:text-xl`) DID reach the title — a headless computed-
 * style check measured it at 20px — but stepping `text-lg` (18px) → `text-xl`
 * (20px), only ~4px above the 16px body, is visually imperceptible, so it looked
 * like the class never applied. The fix is twofold: (1) target ANY descendant
 * `<p>` (`[&_p]`) rather than guessing the exact `>div>div>p` depth, so a future
 * library layout change can't silently unmatch it; (2) use Tailwind's `!`
 * important so the size wins outright over the library's `text-sm` regardless of
 * cascade layer / specificity. `!text-2xl` (24px) is 1.5× the 16px body — clearly
 * a title — and the extra height is tracked by the shell's `--app-banner-h`
 * ResizeObserver. The library keeps `font-semibold` (bold title) untouched. The
 * body/link stay at `text-base` directly on the message `<span>` we own (the link
 * inherits it, so it matches the body).
 */
const TITLE_TYPOGRAPHY = "[&_p]:!text-2xl";
const BODY_TYPOGRAPHY = "text-base";

/**
 * D3 dismissal key: `skycrm-announcement-dismissed:<id>:<updated_at>`. The
 * updated_at segment means an edited/retargeted announcement (whose parent-row
 * update bumps updated_at) gets a fresh key and re-shows for a prior dismisser.
 */
export function announcementDismissalKey(id: string, updatedAt: string): string {
  return `${DISMISS_PREFIX}:${id}:${updatedAt}`;
}

/** An internal SPA route (D6) — `/`-prefixed. The server rejects protocol-relative `//`. */
function isInternalLink(url: string): boolean {
  return url.startsWith("/");
}

function readDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    // localStorage unavailable (private mode / disabled) → treat as not dismissed.
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // Best-effort persistence; a failure just means it re-shows on reload.
  }
}

export function AnnouncementBanner() {
  const instanceId = getInstanceIdFromUrl() ?? "";
  const navigate = useNavigate();

  // Only an operator-pack user actively in CRM mode should fire the /active read
  // — the same gate the sibling banners use so no stray request goes out for
  // users who can't see the CRM surface.
  const { hasOperatorPack, currentMode, isLoading: packsLoading } = usePacks();
  const gated = !packsLoading && hasOperatorPack && currentMode === "operator_crm";

  const { data } = useGetActiveAnnouncementsQuery({ instanceId }, { skip: !gated || !instanceId });

  // A tick to re-render after a dismissal writes localStorage (the filter below
  // reads localStorage on every render, so a bumped tick re-evaluates it).
  const [, setTick] = useState(0);

  if (!gated || !instanceId) return null;

  const announcements = data?.announcements ?? [];

  // D3 then D4: drop dismissed, THEN cap at 3 (the endpoint already ordered
  // newest-first, so slice keeps the newest survivors).
  const visible = announcements
    .filter((a) => !readDismissed(announcementDismissalKey(a.id, a.updated_at)))
    .slice(0, MAX_STACKED);

  if (visible.length === 0) return null;

  const handleDismiss = (a: ActiveAnnouncement) => {
    writeDismissed(announcementDismissalKey(a.id, a.updated_at));
    setTick((t) => t + 1);
  };

  const renderLink = (a: ActiveAnnouncement) => {
    if (!a.link_url || !a.link_label) return null;
    const linkClass = "ml-2 font-semibold underline underline-offset-2 hover:opacity-80";
    if (isInternalLink(a.link_url)) {
      const to = a.link_url;
      // Internal → same-tab client-side navigation (no target=_blank).
      return (
        <a
          href={to}
          className={linkClass}
          onClick={(e) => {
            // Respect modified/non-primary clicks (cmd/ctrl/shift/alt or a
            // middle/right button) — let the browser open a new tab/window
            // natively rather than hijacking into the SPA router.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
              return;
            }
            e.preventDefault();
            navigate(to);
          }}
        >
          {a.link_label}
        </a>
      );
    }
    // External → only https:// is safe to emit as a live anchor. The server D6
    // validator already guarantees this, but a row written out-of-band (direct
    // DB / a future importer) could carry e.g. javascript:/http: — render such a
    // link as inert text so it can never become a dangerous live anchor.
    if (!a.link_url.toLowerCase().startsWith("https://")) {
      return <span className={linkClass}>{a.link_label}</span>;
    }
    // External https:// → new tab, hardened against reverse-tabnabbing.
    return (
      <a
        href={a.link_url}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {a.link_label}
      </a>
    );
  };

  return (
    <div data-testid="announcement-banner-stack">
      {visible.map((a) => (
        <div
          key={a.id}
          className="relative"
        >
          <SkyMessageBar
            banner
            intent={a.intent}
            title={a.title}
            message={
              <span className={BODY_TYPOGRAPHY}>
                {a.body}
                {renderLink(a)}
              </span>
            }
            // The ✕ is an overlay (below) rather than SkyMessageBar's own
            // onDismiss: for a title+body banner the library switches to a
            // multiline column layout and drops its built-in dismiss into a
            // bottom-right row (self-end), which reads as vertically
            // mis-centered. The overlay ✕ stays centered against the whole
            // banner; when dismissible, symmetric `px-12` reserves ✕ clearance
            // on BOTH edges so the centered content never runs under the ✕ AND
            // stays visually centered in the available width (twMerge overrides
            // the library's px-4).
            className={
              a.dismissible
                ? `px-12 ${CENTER_CONTENT} ${TITLE_TYPOGRAPHY}`
                : `${CENTER_CONTENT} ${TITLE_TYPOGRAPHY}`
            }
          />
          {a.dismissible && (
            <SkyButton
              appearance="transparent"
              size="small"
              icon={DismissIcon}
              aria-label="Dismiss message"
              onClick={() => handleDismiss(a)}
              className="absolute top-1/2 right-2 -translate-y-1/2"
            />
          )}
        </div>
      ))}
    </div>
  );
}
